// Peer-to-peer voice channel: plain WebRTC between browsers (mesh - every
// participant connects directly to every other one), with Firestore itself
// used as the signaling transport instead of a dedicated WebSocket/media
// server. Consistent with the rest of this app (which already leans on
// Firestore + onSnapshot for everything real-time, see
// WorkspaceDetailPage.js) and needs no new backend infrastructure or paid
// service - the tradeoff is that it only scales to small groups (every
// participant uploads their own audio to every other one directly) and has
// no TURN server, so a user behind a very restrictive NAT/firewall may not
// be able to connect. Good enough for a small team's voice channel; a large
// call would need a real SFU (e.g. a managed provider) instead.
import {
  addDoc, collection, deleteDoc, doc, onSnapshot, query, setDoc, where
} from 'firebase/firestore';
import { db } from './firebase';

const ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' }
];

const HEARTBEAT_INTERVAL_MS = 8000;
// A participant doc older than this (no heartbeat) is treated as a stale
// leftover from a closed tab/crash rather than someone still present -
// Firestore has no built-in "clean this up when the client disconnects"
// the way Realtime Database's onDisconnect() does, so this is the
// self-contained substitute.
const STALE_AFTER_MS = 20000;

function participantsCollection(workspaceId) {
  return collection(db, 'workspaces', workspaceId, 'voiceParticipants');
}

function signalsCollection(workspaceId) {
  return collection(db, 'workspaces', workspaceId, 'voiceSignals');
}

/**
 * Joins a workspace's voice channel. Returns a controller with leave() and
 * setMuted(bool); calls back into the caller for participant list changes,
 * remote audio streams arriving/leaving, and fatal errors (mic permission
 * denied, etc).
 */
export async function joinVoiceChannel({
  workspaceId, uid, displayName, onParticipantsChange, onRemoteStream, onError
}) {
  let localStream;
  try {
    localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (error) {
    onError?.(error);
    return null;
  }

  const peerConnections = new Map(); // remoteUid -> RTCPeerConnection
  const pendingCandidates = new Map(); // remoteUid -> RTCIceCandidateInit[] queued before remote description is set
  let knownParticipants = [];
  let muted = false;
  let closed = false;

  const sendSignal = (to, type, payload) => addDoc(signalsCollection(workspaceId), {
    from: uid,
    to,
    type,
    payload: JSON.stringify(payload),
    createdAt: Date.now()
  }).catch(() => {});

  const removePeer = (remoteUid) => {
    const pc = peerConnections.get(remoteUid);
    if (pc) {
      pc.close();
      peerConnections.delete(remoteUid);
    }
    pendingCandidates.delete(remoteUid);
    onRemoteStream?.(remoteUid, null);
  };

  const createPeerConnection = (remoteUid, isInitiator) => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    localStream.getTracks().forEach((track) => pc.addTrack(track, localStream));

    pc.onicecandidate = (event) => {
      if (event.candidate) sendSignal(remoteUid, 'candidate', event.candidate.toJSON());
    };
    pc.ontrack = (event) => onRemoteStream?.(remoteUid, event.streams[0]);
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed') removePeer(remoteUid);
    };

    peerConnections.set(remoteUid, pc);

    if (isInitiator) {
      pc.createOffer()
        .then((offer) => pc.setLocalDescription(offer).then(() => sendSignal(remoteUid, 'offer', offer)))
        .catch(() => {});
    }

    return pc;
  };

  const flushPendingCandidates = async (remoteUid, pc) => {
    const queued = pendingCandidates.get(remoteUid);
    if (!queued) return;
    pendingCandidates.delete(remoteUid);
    for (const candidate of queued) {
      await pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
    }
  };

  const handleSignal = async (signalDoc) => {
    const { from, type, payload } = signalDoc;
    const data = JSON.parse(payload);

    if (type === 'offer') {
      const pc = peerConnections.get(from) || createPeerConnection(from, false);
      await pc.setRemoteDescription(new RTCSessionDescription(data));
      await flushPendingCandidates(from, pc);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendSignal(from, 'answer', answer);
      return;
    }

    const pc = peerConnections.get(from);
    if (!pc) return;

    if (type === 'answer') {
      await pc.setRemoteDescription(new RTCSessionDescription(data)).catch(() => {});
      await flushPendingCandidates(from, pc);
    } else if (type === 'candidate') {
      if (pc.remoteDescription) {
        await pc.addIceCandidate(new RTCIceCandidate(data)).catch(() => {});
      } else {
        const queue = pendingCandidates.get(from) || [];
        queue.push(data);
        pendingCandidates.set(from, queue);
      }
    }
  };

  const unsubscribeSignals = onSnapshot(
    query(signalsCollection(workspaceId), where('to', '==', uid)),
    (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        if (change.type !== 'added') return;
        const signalDoc = change.doc.data();
        handleSignal(signalDoc);
        deleteDoc(change.doc.ref).catch(() => {});
      });
    }
  );

  const unsubscribeParticipants = onSnapshot(participantsCollection(workspaceId), (snapshot) => {
    const now = Date.now();
    const live = snapshot.docs
      .map((d) => d.data())
      .filter((p) => now - (p.lastSeen || 0) < STALE_AFTER_MS);

    const liveUids = new Set(live.map((p) => p.uid));
    knownParticipants.forEach((p) => {
      if (!liveUids.has(p.uid) && p.uid !== uid) removePeer(p.uid);
    });

    live.forEach((p) => {
      if (p.uid === uid || peerConnections.has(p.uid)) return;
      // Deterministic initiator per pair (lower uid calls the other) avoids
      // both sides simultaneously offering to each other (glare).
      if (uid < p.uid) createPeerConnection(p.uid, true);
    });

    knownParticipants = live;
    onParticipantsChange?.(live.filter((p) => p.uid !== uid));
  });

  await setDoc(doc(participantsCollection(workspaceId), uid), {
    uid,
    displayName,
    mutedByUser: false,
    lastSeen: Date.now()
  });

  const heartbeat = setInterval(() => {
    setDoc(doc(participantsCollection(workspaceId), uid), { lastSeen: Date.now() }, { merge: true }).catch(() => {});
  }, HEARTBEAT_INTERVAL_MS);

  const leave = async () => {
    if (closed) return;
    closed = true;
    clearInterval(heartbeat);
    unsubscribeSignals();
    unsubscribeParticipants();
    peerConnections.forEach((pc) => pc.close());
    peerConnections.clear();
    localStream.getTracks().forEach((track) => track.stop());
    await deleteDoc(doc(participantsCollection(workspaceId), uid)).catch(() => {});
  };

  // Best-effort - a closed tab can't await the deleteDoc above, so the
  // heartbeat staleness check is what actually guarantees cleanup for
  // everyone else even when this fires too late or not at all.
  window.addEventListener('beforeunload', leave);

  const setMuted = (value) => {
    muted = value;
    localStream.getAudioTracks().forEach((track) => { track.enabled = !muted; });
    setDoc(doc(participantsCollection(workspaceId), uid), { mutedByUser: muted }, { merge: true }).catch(() => {});
  };

  return {
    leave: async () => {
      window.removeEventListener('beforeunload', leave);
      await leave();
    },
    setMuted
  };
}
